class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.maxProducts = 8;
    this.productContainer = this.querySelector('.product-grid');
    this.fallbackTemplate = this.querySelector('#fallback-products-template');
    this.fallbackTitle = this.getAttribute('fallback-title') || '';
    this.fallbackCollection = this.getAttribute('fallback-collection') || '';
    this.productCardSection = 'card-product';
  }

  connectedCallback() {
    this.loadProducts();
  }

  loadProducts() {
    const recentlyViewedProducts = this.getRecentlyViewedProducts();

    if (recentlyViewedProducts.length > 0) {
      this.fetchRecentlyViewedProducts(recentlyViewedProducts);
    } else {
      this.useFallbackProducts();
    }
  }

  getRecentlyViewedProducts() {
    const recentlyViewed = JSON.parse(localStorage.getItem('recently-viewed-products') || '[]');
    // Filter out the current product if we're on a product page
    const currentProductHandle = this.getCurrentProductHandle();
    return recentlyViewed
      .filter(handle => (handle !== currentProductHandle) && (handle !== 'gift-card'))
      .slice(0, this.maxProducts);
  }

  getCurrentProductHandle() {
    const path = window.location.pathname;
    if (path.includes('/products/')) {
      return path.split('/products/')[1].split('?')[0];
    }
    return '';
  }

  async fetchRecentlyViewedProducts(productHandles) {
    this.productContainer.innerHTML = '';
    this.classList.add('loading');

    try {
      const promises = productHandles.map(handle => this.fetchProductCard(handle));
      const results = await Promise.allSettled(promises);

      const successfulResults = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      if (successfulResults.length > 0) {
        successfulResults.forEach((productCard, index) => {
          const cardHeading = productCard.querySelector('.card__heading')
          const exampleProductTitle = window.cardProductStrings?.exampleProductTitle || '';

          if (cardHeading && cardHeading.textContent.trim() === exampleProductTitle.trim()) {
            return;
          }

          const gridItem = document.createElement('li');
          gridItem.id = `Slide-CartDrawer-${index}`;
          gridItem.classList.add('grid__item', 'slider__slide');
          gridItem.appendChild(productCard);
          this.productContainer.appendChild(gridItem);
        });

        initLazyImages();

        this.initSlider();
        this.enableModification();
      } else {
        this.useFallbackProducts();
      }
    } catch (error) {
      console.error('Error fetching recently viewed products:', error);
      this.useFallbackProducts();
    } finally {
      this.classList.remove('loading');
    }
  }

  async fetchProductCard(handle) {
    try {
      const url = `/products/${handle}?section_id=${this.productCardSection}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productCard = html.querySelector('.card-wrapper');

      if (!productCard) {
        throw new Error(`Product card not found for ${handle}`);
      }

      return productCard;
    } catch (error) {
      console.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  }

  useFallbackProducts() {
    if (this.fallbackTemplate) {
      const fallbackTitle = this.fallbackTitle || 'You might also like';
      const headingElement = this.querySelector('.recently-viewed-products__heading');

      if (headingElement) {
        headingElement.textContent = fallbackTitle;
      }

      const content = this.fallbackTemplate.content.cloneNode(true);
      this.productContainer.innerHTML = '';
      this.productContainer.appendChild(content);
      this.initSlider();
      this.enableModification();
    } else {
      this.remove();
    }
  }

  initSlider() {
    if (this.productContainer && this.productContainer.children.length > 0) {
      const template = document.createElement('template');
      template.innerHTML = `<card-slider class="slider-mobile-gutter slider-component-desktop" disable-animations></card-slider>`;

      const fragment = template.content.cloneNode(true);
      const slider = fragment.querySelector('card-slider');

      const sliderButtons = this.querySelector('.slider-buttons');

      slider.appendChild(this.productContainer);

      if (sliderButtons) {
        slider.appendChild(sliderButtons);
      }

      this.appendChild(slider);
    }
  }

  enableModification() {
    const handleMiniProductForm = (e) => {
      e.preventDefault();
      const link = e.target.closest('.quickshop__option-variant-link') || e.target.closest('.product-card-link');
      const url = link.href;

      if (!url || !url.includes('/products/')) return;

      const productHandle = url.split('/products/')[1].split('?')[0];
      let variantId = url.split('?variant=')[1];

      if (!variantId) {
        const cardContent = link.closest('.card__content');
        const firstSelectedOption = cardContent.querySelector('.quickshop__option-variant-link');
        variantId = firstSelectedOption.href.split('?variant=')[1];
      }

      const miniProductForm = new MiniProductForm(productHandle, variantId, {
        onOpen: (drawer) => {
          const variantRadios = drawer.querySelector('variant-radios');
          if (variantRadios) {
            variantRadios.removeAttribute('data-modify-form');
            variantRadios.setAttribute('data-mini-product-form', 'true');
          }

          const addButton = drawer.querySelector('[name="add"] span');
          if (addButton && addButton.textContent?.trim() !== window.variantStrings.soldOut) {
            addButton.textContent = window.variantStrings.addToCart;
          }

          console.debug('Mini product form opened', productHandle, variantId);
        },
        onClose: () => {
          console.debug('Mini product form closed', productHandle, variantId);
        }
      });

      // Fetch and open the drawer
      miniProductForm.fetchAndOpenDrawer();

      // Subscribe to the cart update event
      const cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form') {
          miniProductForm.closeDrawer();

          cartUpdateUnsubscriber();
        }
      });
    }

    this.addEventListener('click', (e) => {
      if (e.target.closest('.quickshop__option-variant-link') || e.target.closest('.product-card-link')) {
        handleMiniProductForm(e);
      }
    });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
